'use client';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import useFooterStore from '@/store/footer';
import { figtree } from '@/metadata/next-fonts/figtree';
import { getDataLayer } from '@/utils/data-layer';
import CookieConsent from '@/components/CookieConsent';
import useHeaderStore from '@/store/header';
import dynamic from 'next/dynamic';
import { IProductDetails } from '@/interfaces/product';
import { ErrorBoundary } from 'react-error-boundary';
import { useBundleUnlockToast } from '@/hooks/bundlePromotion';

const Modal = dynamic(() => import('@/components/Modal'), { ssr: false });
const ProductDetailImage = dynamic(() => import('@/components/ProductDetailImage'), { ssr: false });
interface AppContainerProps {
  children: React.ReactNode;
}

export default function AppContainer({ children }: Readonly<AppContainerProps>) {
  const pathname = usePathname();
  const activateOverlay = useHeaderStore((state) => state.activateOverlay);
  const openQuickView = useHeaderStore((state) => state.openQuickView);
  const quickView = useHeaderStore((state) => state.quickView);
  const quickViewLoading = useHeaderStore((state) => state.quickViewLoading);
  const quickViewProduct = useHeaderStore((state) => state.quickViewProduct);
  const setQuickViewSeName = useHeaderStore((state) => state.setQuickViewSeName);
  const setQuickViewProduct = useHeaderStore((state) => state.setQuickViewProduct);
  const [productOptions, setProductOptions] = useState<any>(null);
  const setShowFullFooter = useFooterStore((state) => state.setShowFullFooter);
  const hasQuickViewProduct = Object.keys(quickViewProduct).length > 0;

 useBundleUnlockToast();

  useEffect(() => {
    if (quickView && quickViewProduct) {
      const ProductOptions = dynamic(() => import('@/components/ProductOptions'));
      setProductOptions(
        <ErrorBoundary
          onError={(error) => {
            console.error('AppContainer: ProductOptions error', error);
          }}
          fallback={null}
        >
          <ProductOptions
            setProductCardSeName={setQuickViewSeName}
            quickView={true}
            product={quickViewProduct}
            productInfoGraphic={quickViewProduct?.ProductViewed}
          />
        </ErrorBoundary>,
      );
    }
  }, [quickViewProduct]);

  useEffect(() => {
    // lazyLoadImages();
    const handleScroll = () => {
      if (window.scrollY > 10) {
        document.body.classList.add('scrolled');
      } else {
        document.body.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    activateOverlay(false);
    openQuickView(false);
    setQuickViewProduct({} as IProductDetails);
    setQuickViewSeName('');
    if (globalThis.window !== undefined) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  useEffect(() => {
    if (!pathname?.includes('/category/') && !pathname?.includes('/brand/')) {
      setShowFullFooter(true);
    } else {
      setShowFullFooter(false);
    }
    const dataLayer = getDataLayer();
    dataLayer?.push({
      event: 'page_view',
      ecommerce: {
        pagePath: pathname,
      },
    });
  }, [pathname]);

  return (
    <main className={'flex flex-col ' + figtree.className} id="app-main">
      {children}
      <CookieConsent />
      <div id="portal" />
      {quickView && (
        <ErrorBoundary
          onError={(error) => {
            console.error('AppContainer: Modal error', error);
          }}
          fallback={null}
        >
          <Modal
            className="relative p-0"
            contentClass="w-full "
            open={quickView}
            heading="Quick View"
            closeModal={() => {
              openQuickView(false);
              setQuickViewProduct({} as IProductDetails);
              setQuickViewSeName('');
              setProductOptions(null);
            }}
          >
            <Suspense fallback={<QuickViewLoading />}>
              {quickViewLoading || (hasQuickViewProduct && !productOptions) ? (
                <QuickViewLoading />
              ) : (
                <>
                  {hasQuickViewProduct ? (
                    <div className="gap-8 md:flex">
                      <div className="product-images w-full md:w-[600px] lg:w-[620px] xl:w-[630px]">
                        <ErrorBoundary
                          onError={(error) => {
                            console.error('AppContainer: ProductDetailImage error', error);
                          }}
                          fallback={null}
                        >
                          <ProductDetailImage quickView={true} productDetail={quickViewProduct} />
                        </ErrorBoundary>
                      </div>
                      <div className="relative -mr-8 mt-5 w-[444px] overflow-y-auto pr-8 md:mt-0 md:w-[400px] lg:w-[420px] xl:w-[444px]">
                        {productOptions && productOptions}
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-[548px] items-center lg:min-w-[1000px]">
                      <div className="absolute bottom-0 left-0 right-0 top-0 z-[10000] flex h-full flex-col items-center justify-center p-5">
                        <p className="text-center text-3xl">Product detail is not available.</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Suspense>
          </Modal>
        </ErrorBoundary>
      )}
    </main>
  );
}

const QuickViewLoading = () => {
  return (
    <div className="flex min-h-[548px] items-center lg:min-w-[1000px]">
      <div className="absolute bottom-0 left-0 right-0 top-0 z-[10000] flex h-full flex-col items-center justify-center p-5">
        <p className="mb-2 text-center text-3xl"> Quick view product is loading</p>
        <button title="Quick view product is loading" className="loading p-5"></button>
      </div>
    </div>
  );
};
